import { create } from 'zustand';
import { AvailableCarrierItem, LargeOrderFeeLine, Order } from '@/types';

export interface SelectedCarrier extends LargeOrderFeeLine {
  carrier_slug: string;
  carrier_name: string;
  carrier_logo_url: string;
  shipping_fee: number;
  delivery_day: 'same' | 'next';
  same_day_cutoff: string | null;
  drop_off_time: string | null;
}

interface CarrierSelectionData {
  process_id: string;
  recommended_carrier_slug: string;
  available_carriers: AvailableCarrierItem[];
}

export interface ContactDisplayInfo {
  provinceName: string;
  areaName: string;
  /**
   * SVC-STEP — the destination's province CODE, carried beside the name it was
   * derived from.
   *
   * QA INC2-F2: the summary's service trace needs the code to ask the resolver
   * "what ships to HERE", and it used to read `savedProvinceCode`, which ONLY
   * the create flow ever wrote. An edited order therefore resolved
   * destination-blind: the edit nav skipped correctly on a province rule while
   * the summary attributed the account default — wrong service AND wrong
   * reason, on the one line that exists to be trustworthy.
   *
   * It lives here rather than in a second store field because both flows
   * already write this object, at the same moment, from the same lookup. A
   * separate field for the same fact is the two-readers shape wearing a store
   * key — which is exactly how it diverged the first time.
   *
   * Optional: an older cached store shape, or any caller that has not been
   * updated, degrades to a destination-blind answer rather than crashing.
   */
  provinceCode?: string | null;
}

interface NewOrderStore {
  orderPayload: Order | null;
  selectedCarrier: SelectedCarrier | null;
  carrierSelection: CarrierSelectionData | null;
  sourceRegionUid: string | null;
  contactDisplayInfo: ContactDisplayInfo | null;
  savedProvinceCode: string | null;
  isEditMode: boolean;
  editOrderUid: string | null;
  setOrderPayload: (payload: Order) => void;
  setSelectedCarrier: (carrier: SelectedCarrier | null) => void;
  setCarrierSelection: (data: CarrierSelectionData | null) => void;
  setSourceRegionUid: (uid: string) => void;
  setContactDisplayInfo: (info: ContactDisplayInfo) => void;
  setSavedProvinceCode: (code: string | null) => void;
  setEditMode: (orderUid: string) => void;
  clearEditMode: () => void;
  reset: () => void;
}

const useNewOrderStore = create<NewOrderStore>((set) => ({
  orderPayload: null,
  selectedCarrier: null,
  carrierSelection: null,
  sourceRegionUid: null,
  contactDisplayInfo: null,
  savedProvinceCode: null,
  isEditMode: false,
  editOrderUid: null,
  setOrderPayload: (payload) => set({ orderPayload: payload }),
  setSelectedCarrier: (carrier) => set({ selectedCarrier: carrier }),
  setCarrierSelection: (data) => set({ carrierSelection: data }),
  setSourceRegionUid: (uid) => set({ sourceRegionUid: uid }),
  setContactDisplayInfo: (info) => set({ contactDisplayInfo: info }),
  setSavedProvinceCode: (code) => set({ savedProvinceCode: code }),
  setEditMode: (orderUid) => set({ isEditMode: true, editOrderUid: orderUid }),
  clearEditMode: () => set({ isEditMode: false, editOrderUid: null }),
  reset: () =>
    set({
      orderPayload: null,
      selectedCarrier: null,
      carrierSelection: null,
      sourceRegionUid: null,
      contactDisplayInfo: null,
      savedProvinceCode: null,
      isEditMode: false,
      editOrderUid: null,
    }),
}));

export default useNewOrderStore;
